import { PagingParameters } from "@/api/dtos";
import {
  useCountTrash,
  useDeleteTotal,
  useGetTrash,
  useRemoveFromTrash,
} from "@/api/trash";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { DataTable } from "@/components/ui/datatable";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTrigger,
} from "@/components/ui/dialog";
import { DrawerBottom, DrawerContent } from "@/components/ui/drawer-bottom";
import { Input } from "@/components/ui/input";
import { toast } from "@/hooks/use-toast";
import { Trash } from "@/lib/types";
import { ColumnDef } from "@tanstack/react-table";
import { Trash2Icon } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

const TrashDashboard = () => {
  const { t } = useTranslation();

  const [search, setSearch] = useState("");
  const [idsSelected, setIdsSelected] = useState<string[]>([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [getPagination, setPagination] = useState<PagingParameters>({
    page: 0,
    pageSize: 10,
  });

  const { mutate: removeFromTrash } = useRemoveFromTrash();
  const { mutate: deleteTotal } = useDeleteTotal();

  const { data: count } = useCountTrash({
    ...(search
      ? {
          documentTitle: {
            contains: search,
            mode: "insensitive",
          },
        }
      : {}),
  });

  const { data } = useGetTrash({
    where: {
      documentTitle: {
        contains: search,
        mode: "insensitive",
      },
    },
    orderBy: [],
    take: getPagination.pageSize,
    skip: getPagination.page * getPagination.pageSize,
  });

  const handleSelect = (id: string | undefined) => {
    if (!id) return;

    setIdsSelected((prevIds) => {
      const isSelected = prevIds.includes(id);
      if (isSelected) {
        return prevIds.filter((selectedId) => selectedId !== id);
      } else {
        return [...prevIds, id];
      }
    });
  };

  const columns: ColumnDef<Trash, any>[] = useMemo(() => {
    return [
      {
        accessorKey: "select",
        header: "",
        size: 10,
        cell: ({ row }) => {
          return (
            <Checkbox
              onCheckedChange={() => handleSelect(row.original.id)}
              checked={idsSelected.includes(row.original.id ?? "")}
            />
          );
        },
      },
      {
        cell: ({ row }) =>
          row.original.documentType ? t(row.original.documentType) : "",
        header: t("documentType"),
        size: 100,
      },
      {
        accessorKey: "documentTitle",
        header: t("title"),
        size: 300,
      },
    ];
  }, [idsSelected, t]);

  const handleRemoveFromTrash = () => {
    removeFromTrash(
      {
        id: {
          in: idsSelected,
        },
      },
      {
        onSuccess: () => {
          setIdsSelected([]);
          setOpenDialog(false);
          toast({
            title: t("documentsRestored"),
            variant: "success",
            description: t("documentsHaveBeenRestored"),
          });
        },
        onError: () => {
          toast({
            title: t("error"),
            variant: "destructive",
            description: t("anErrorOccurredWhileRestoringDocuments"),
          });
        },
      },
    );
  };

  const handleDeleteTotal = () => {
    deleteTotal(
      {
        id: {
          in: idsSelected,
        },
      },
      {
        onSuccess: () => {
          setIdsSelected([]);
          setOpenDialog(false);
          toast({
            title: t("documentsDeleted"),
            variant: "success",
            description: t("documentsHaveBeenDeletedFromTrash"),
          });
        },
        onError: () => {
          toast({
            title: t("error"),
            variant: "destructive",
            description: t("anErrorOccurredWhileDeletingDocuments"),
          });
        },
      },
    );
  };

  return (
    <div className="px-[80px] py-[48px]">
      <div className="flex flex-row justify-between items-center">
        <div className="flex flex-row items-center gap-2">
          <p className="text-gray-900 text-[32px] font-semibold font-[Instrument Sans] leading-normal">
            {t("trash")}
          </p>
          <Badge variant="outlinedActive">{count}</Badge>
        </div>
        <div className="w-[300px]">
          <Input
            placeholder={t("search")}
            onChange={(s) => setSearch(s.target.value)}
          />
        </div>
      </div>
      <div className="w-full">
        <DataTable
          pagination={{
            pagination: getPagination,
            setPage: setPagination,
            totalSize: count,
          }}
          columns={columns}
          data={data ?? []}
          options={{
            enableColumnResizing: true,
          }}
        />
      </div>
      <DrawerBottom open={idsSelected.length > 0}>
        <DrawerContent className="w-fit mx-auto bottom-10 bg-transparent border-none">
          <div className="mx-auto w-fit bg-white flex gap-[80px] p-4 border-6 border-gray-200">
            <div className="flex flex-row justify-between items-center gap-10">
              <p className="text-black text-md font-semibold font-['Raleway'] leading-normal">
                {idsSelected.length} {t("elementsSelected")}
              </p>
              <p
                onClick={() => setIdsSelected([])}
                className="text-primary text-sm font-semibold font-['Raleway'] leading-normal underline cursor-pointer"
              >
                {t("unselectAll")}
              </p>
            </div>
            <div className="flex flex-row justify-between items-center gap-2">
              <Dialog open={openDialog} onOpenChange={setOpenDialog}>
                <DialogTrigger asChild>
                  <Button
                    variant="destructive"
                    onClick={() => setOpenDialog(true)}
                  >
                    <Trash2Icon className="w-4 h-4 mr-2" />
                    {t("bulkDelete")}
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-white w-fit">
                  <p className="text-black text-md font-semibold font-['Raleway'] leading-normal text-center w-[300px] mx-auto">
                    {t("areYouSureYouWantToDeleteTheseElements")}
                  </p>
                  <DialogFooter className="flex flex-row justify-between items-center gap-2 mt-3">
                    <Button
                      variant="ghost"
                      className="border w-full"
                      onClick={() => setOpenDialog(false)}
                    >
                      {t("cancel")}
                    </Button>
                    <Button
                      variant="destructive"
                      className="w-full"
                      onClick={handleDeleteTotal}
                    >
                      {t("delete")}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
              <Button variant="default" onClick={handleRemoveFromTrash}>
                {t("bulkRestore")}
              </Button>
            </div>
          </div>
        </DrawerContent>
      </DrawerBottom>
    </div>
  );
};

export default TrashDashboard;
